import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { applyServerErrors, TextField } from '../../../components/ui/Field';
import { ErrorState } from '../../../components/ui/Page';
import { Skeleton } from '../../../components/ui/Skeleton';
import { ApiError } from '../../../services/http';
import { readPreference, writePreference } from '../../../utils/storage';
import { authApi } from '../api';
import { AuthLayout, takeFragmentToken } from './AuthLayout';

export const newPassword = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(128, 'Use at most 128 characters');

export function ForgotPasswordPage() {
  const [sent, setSent] = useState<string | null>(null);
  const schema = z.object({
    workspace: z.string().trim().toLowerCase().optional(),
    email: z.string().trim().email('Enter a valid email'),
  });
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { workspace: readPreference('workspace') ?? '', email: '' },
  });
  const mutation = useMutation({ mutationFn: authApi.forgot, onSuccess: (res) => setSent(res.message) });

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link that works for 30 minutes."
      footer={<Link to="/login">Back to sign in</Link>}
    >
      {sent ? (
        <p role="status" className="text-sm text-fg">
          {sent}
        </p>
      ) : (
        <form
          noValidate
          className="space-y-4"
          onSubmit={form.handleSubmit((v) =>
            mutation.mutate({ email: v.email, ...(v.workspace ? { workspace: v.workspace } : {}) }),
          )}
        >
          {mutation.error ? <ErrorState error={mutation.error} /> : null}
          <TextField
            label="Workspace"
            hint="Leave empty for platform administrator accounts"
            {...form.register('workspace')}
          />
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />
          <Button type="submit" className="w-full" loading={mutation.isPending}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

const resetSchema = z
  .object({ newPassword, confirm: z.string() })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'Passwords do not match' });

export function ResetPasswordPage() {
  const [token] = useState(takeFragmentToken);
  const [done, setDone] = useState(false);
  const form = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: { newPassword: '', confirm: '' },
  });
  const mutation = useMutation({
    mutationFn: (v: { newPassword: string }) => authApi.reset({ token: token!, newPassword: v.newPassword }),
    onSuccess: () => setDone(true),
    onError: (err) => err instanceof ApiError && applyServerErrors(err.fieldErrors, form.setError as never),
  });

  if (!token) {
    return (
      <AuthLayout title="Link not valid" footer={<Link to="/forgot-password">Request a new link</Link>}>
        <p className="text-sm text-fg-muted">
          This reset link is incomplete. Open the link from your email again.
        </p>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Choose a new password" footer={<Link to="/login">Back to sign in</Link>}>
      {done ? (
        <div role="status" className="space-y-4 text-sm">
          <p>Your password was changed and you were signed out everywhere.</p>
          <Link to="/login" className="font-medium text-brand-700 hover:underline">
            Sign in
          </Link>
        </div>
      ) : (
        <form noValidate className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))}>
          {mutation.error &&
          !(mutation.error instanceof ApiError && mutation.error.code === 'VALIDATION_FAILED') ? (
            <ErrorState error={mutation.error} />
          ) : null}
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            hint="At least 10 characters"
            error={form.formState.errors.newPassword?.message}
            {...form.register('newPassword')}
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            error={form.formState.errors.confirm?.message}
            {...form.register('confirm')}
          />
          <Button type="submit" className="w-full" loading={mutation.isPending}>
            Save password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

const acceptSchema = z
  .object({
    fullName: z.string().trim().min(1, 'Enter your name').max(150),
    password: newPassword,
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'Passwords do not match' });

export function AcceptInvitePage() {
  const [token] = useState(takeFragmentToken);
  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => authApi.previewInvitation(token!),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
  const form = useForm<z.infer<typeof acceptSchema>>({
    resolver: zodResolver(acceptSchema),
    values: { fullName: preview.data?.fullName ?? '', password: '', confirm: '' },
  });
  const mutation = useMutation({
    mutationFn: (v: z.infer<typeof acceptSchema>) =>
      authApi.acceptInvitation({ token: token!, fullName: v.fullName, password: v.password }),
    onSuccess: (res) => {
      if (res.workspace) writePreference('workspace', res.workspace);
    },
    onError: (err) => err instanceof ApiError && applyServerErrors(err.fieldErrors, form.setError as never),
  });

  if (!token || preview.isError) {
    return (
      <AuthLayout title="Invitation not valid" footer={<Link to="/login">Go to sign in</Link>}>
        <p className="text-sm text-fg-muted">
          This invitation link is invalid, expired or already used. Ask your administrator to send a new one.
        </p>
      </AuthLayout>
    );
  }
  if (preview.isPending) {
    return (
      <AuthLayout title="Accept invitation">
        <div className="space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-9 w-full" />
        </div>
      </AuthLayout>
    );
  }
  if (mutation.isSuccess) {
    return (
      <AuthLayout title="You're all set">
        <div role="status" className="space-y-4 text-sm">
          <p>Your account is active. Sign in to continue.</p>
          <Link
            to={preview.data.isPlatformAdmin ? '/platform/login' : '/login'}
            className="font-medium text-brand-700 hover:underline"
          >
            Sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout
      title={`Join ${preview.data.tenantName ?? 'the platform console'}`}
      subtitle={`Invitation for ${preview.data.email}${preview.data.workspace ? ` · workspace "${preview.data.workspace}"` : ''}`}
    >
      <form noValidate className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))}>
        {mutation.error &&
        !(mutation.error instanceof ApiError && mutation.error.code === 'VALIDATION_FAILED') ? (
          <ErrorState error={mutation.error} />
        ) : null}
        <TextField
          label="Your name"
          autoComplete="name"
          error={form.formState.errors.fullName?.message}
          {...form.register('fullName')}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          hint="At least 10 characters"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <TextField
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          error={form.formState.errors.confirm?.message}
          {...form.register('confirm')}
        />
        <Button type="submit" className="w-full" loading={mutation.isPending}>
          Activate account
        </Button>
      </form>
    </AuthLayout>
  );
}
